import React, { useContext, useCallback, useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import qs from 'qs';
import PropTypes from 'prop-types';
import { capitalize } from '../../../../utils';
import { RegisterTypes } from '../tools/constants';
import { RegistrationContext } from '../../../../contexts';
import { RegistrationActions } from '../../../../contexts/RegistrationContext/RegistrationReducer';
import { FancyField, Checkbox } from '../../../../components/fancyFieldsV2';
import { JerseySelector, Slider } from '../../../../components';
import { CustomDropdownIndicator } from '.';
import classNames from 'classnames/bind';
import styles from '../../../../styles/main.module.scss';

const { UPDATE_NEW_GROUP_DETAILS, UPDATE_NEW_TEAM_DETAILS } = RegistrationActions;

const cx = classNames.bind(styles);

const GroupTeamForm = ({ groupType, data }) => {
  const [showPassword, setShowPassword] = useState(false);

  const [registerState, registerDispatch] = useContext(RegistrationContext);

  const { league } = registerState;
  const {
    groups = [],
    registration: { jersey_colors, max_team_size },
  } = league || {};

  const history = useHistory();
  const { location } = history || {};
  const { search } = location;

  const urlParams = qs.parse(search, { ignoreQueryPrefix: true });
  const { type } = urlParams || {};

  useEffect(() => {
    if (!type) {
      history.replace({
        search: qs.stringify({
          type:
            groupType === 'team' ? RegisterTypes.PREPAID_TEAM_CAPTAIN : RegisterTypes.GROUP_CAPTAIN,
        }),
      });
    }
  }, [history, type]);

  const { name, color, sizeEstimate, password, passwordHint } = data || {};

  const omitColors =
    jersey_colors.length === 1 ? [] : groups?.map(c => c.color)?.filter(c => !!c.name);

  const updateGroupTeam = useCallback(
    update => {
      registerDispatch({
        update,
        type: groupType === 'team' ? UPDATE_NEW_TEAM_DETAILS : UPDATE_NEW_GROUP_DETAILS,
      });
    },
    [registerDispatch]
  );

  return (
    <>
      <div className={cx('register-field')}>
        <h2>{capitalize(groupType)} Name</h2>
        <FancyField
          blockStyle
          className="w-100"
          noLabel
          placeholder={`Type ${groupType} name here`}
          value={name}
          onChange={v => updateGroupTeam({ name: v })}
        />
      </div>
      <div className={cx('register-field')}>
        <h2>
          Estimated {capitalize(groupType)} Size{' '}
          <span className={cx('letter-spacing-1')}>{sizeEstimate} - Players</span>
        </h2>
        {max_team_size !== 1 && (
          <div className="px-2">
            <Slider
              value={sizeEstimate || 0}
              onChange={val => updateGroupTeam({ sizeEstimate: val })}
              min={1}
              max={max_team_size}
            />
          </div>
        )}
      </div>
      {jersey_colors?.length > 0 && (
        <div className={cx('register-field')}>
          <h2>Request a T-Shirt Color</h2>
          <JerseySelector
            omitColors={omitColors}
            options={jersey_colors}
            onSelect={col => updateGroupTeam({ color: col })}
            selectedColor={color}
            CustomDropdownIndicator={CustomDropdownIndicator}
            hideSeparator
            borderRadius="4px"
          />
        </div>
      )}
      {groupType === 'team' && (
        <>
          <div className={cx('register-field')}>
            <h2>{capitalize(groupType)} Password</h2>
            <div>
              <FancyField
                blockStyle
                name="password"
                type={showPassword ? 'text' : 'password'}
                value={password || ''}
                onChange={v => updateGroupTeam({ password: v })}
                placeholder="Start typing here"
                noLabel
                className="my-4"
              />
              <div className={cx('password-footnote')}>
                <small>
                  Remember to jot down this password to send it along to your teammates!
                </small>
              </div>
              <Checkbox
                label="Show Password"
                value={showPassword}
                onChange={val => setShowPassword(val)}
              />
            </div>
          </div>
          <div className={cx('register-field')}>
            <h2 className="justify-content-start">
              Password Hint{' '}
              <span className={cx('ml-2', 'letter-spacing-1')}>
                <i>(Optional)</i>
              </span>
            </h2>
            <div>
              <FancyField
                blockStyle
                className="w-100"
                noLabel
                placeholder="Start typing here"
                value={passwordHint}
                onChange={v => updateGroupTeam({ passwordHint: v })}
              />
            </div>
          </div>
        </>
      )}
    </>
  );
};

GroupTeamForm.propTypes = {
  groupType: PropTypes.string,
  data: PropTypes.shape({
    name: PropTypes.string,
    color: PropTypes.shape({
      hex: PropTypes.string,
      name: PropTypes.string,
    }),
    sizeEstimate: PropTypes.number,
    password: PropTypes.string,
    passwordHint: PropTypes.string,
  }),
};

GroupTeamForm.defaultProps = {
  groupType: 'group',
  data: {},
};

export default GroupTeamForm;
